import { Component, Inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';

import { VendaTipoDocumentoEnum } from '@shared/enums/vendas/venda-tipo-documento.enum';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { ProcessoVendaService } from '@shared/services/apis/vendas/processo-venda.service';

/** Documento de Vendas vinculado e o código da oportunidade, para a pergunta. */
export type DesvincularOportunidadeDialogData = {
  idVenda: number;
  idVendaTipoDocumento: number;
  numero: number;
  codigoOportunidade: string;
};

/**
 * "Desvincular da oportunidade" (RX-03): confirma e remove o vínculo; o documento e a oportunidade continuam,
 * sem alteração. Fecha com `true` quando desvinculou, para quem abriu tirar a marca sem recarregar.
 */
@Component({
  selector: 'app-desvincular-oportunidade-dialog',
  standalone: true,
  imports: [MatButtonModule, MatDialogModule, MatIconModule, MatProgressSpinnerModule, MatTooltipModule],
  templateUrl: './desvincular-oportunidade-dialog.component.html',
  styleUrls: ['./desvincular-oportunidade-dialog.component.css'],
})
export class DesvincularOportunidadeDialogComponent {
  public isDesvinculando = false;

  constructor(
    private dialogRef: MatDialogRef<DesvincularOportunidadeDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: DesvincularOportunidadeDialogData,
    private processoVendaService: ProcessoVendaService,
    private alertService: AlertService,
  ) { }

  /** "Pedido nº 12": o tipo como o usuário fala, como no protótipo. */
  public get subtitulo(): string {
    const tipo = this.data.idVendaTipoDocumento === VendaTipoDocumentoEnum.PedidoDeVenda ? 'Pedido' : 'Orçamento';
    return `${tipo} nº ${this.data.numero}`;
  }

  /** "oportunidade #12"; sem código, a oportunidade foi excluída no CRM e o documento ainda pode sair dela. */
  public get textoOportunidade(): string {
    return this.data.codigoOportunidade ? `oportunidade #${this.data.codigoOportunidade}` : 'oportunidade excluída';
  }

  public get textoSemAlteracao(): string {
    return this.data.codigoOportunidade
      ? 'O documento e a oportunidade continuam, sem alteração.'
      : 'O documento continua, sem alteração.';
  }

  public fechar(): void {
    if (this.isDesvinculando) return;
    this.dialogRef.close(false);
  }

  public desvincular(): void {
    if (this.isDesvinculando) return;

    this.isDesvinculando = true;
    this.processoVendaService.desvincularOportunidade(this.data.idVenda).subscribe({
      next: response => {
        this.isDesvinculando = false;
        if (response.isError) {
          this.alertService.show('Aviso', response.errorDescription, AlertType.warning);
          return;
        }

        this.alertService.show('Sucesso',
          `O documento saiu da ${this.textoOportunidade}. ${this.textoSemAlteracao}`,
          AlertType.success);
        this.dialogRef.close(true);
      },
      error: () => {
        this.isDesvinculando = false;
        this.alertService.show('Aviso',
          'Não foi possível desvincular o documento agora. Tente novamente em alguns instantes.', AlertType.warning);
      },
    });
  }
}
